import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { 
  Shield, 
  Users, 
  Gavel, 
  LayoutGrid, 
  Zap,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../components/common/Button';

export const Welcome = () => {
  const features = [
    {
      icon: Shield,
      title: 'Biometric Security',
      description: 'State-of-the-art WebAuthn integration ensures your identity and assets are protected by your own biology.'
    },
    {
      icon: Users,
      title: 'Social Circles',
      description: 'Connect with like-minded individuals in specialized circles designed for collaboration and growth.'
    },
    {
      icon: Gavel,
      title: 'Decentralized Governance',
      description: 'Every voice matters. Participate in transparent voting and shape the future of the ecosystem.'
    },
    {
      icon: LayoutGrid,
      title: 'Unified App Ecosystem',
      description: 'Access a curated library of applications that share a single identity and data layer.'
    }
  ];

  const services = [
    'Identity Management',
    'Secure Data Vaults',
    'Community Governance Tools',
    'Developer SDKs',
    'Cross-App Communication',
    'Reputation Systems'
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Navigation */}
      <nav className="flex items-center justify-between px-6 py-4 bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-100">
        <div className="flex items-center space-x-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white">
            <LayoutGrid size={20} />
          </div>
          <span className="text-xl font-bold text-slate-900">Keskedo</span>
        </div>
        <div className="hidden md:flex items-center space-x-8 text-sm font-medium text-slate-600">
          <Link to="/features" className="hover:text-indigo-600 transition-colors">Features</Link>
          <Link to="/services" className="hover:text-indigo-600 transition-colors">Services</Link>
          <Link to="/about" className="hover:text-indigo-600 transition-colors">About</Link>
          <Link to="/contact" className="hover:text-indigo-600 transition-colors">Contact</Link>
        </div>
        <div className="flex items-center space-x-4">
          <Link to="/auth/login">
            <Button variant="ghost">Login</Button>
          </Link>
          <Link to="/auth/register">
            <Button>Get Started</Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden bg-slate-50">
        <div className="absolute inset-0 honeycomb-bg opacity-50" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge variant="primary" className="mb-6 px-4 py-1.5 text-sm">Now in Public Beta</Badge>
              <h1 className="text-5xl md:text-6xl font-extrabold text-slate-900 tracking-tight mb-6">
                The Future of <span className="text-indigo-600">Unified Ecosystems</span> is Here.
              </h1>
              <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                Keskedo is a decentralized platform that brings together social connections, 
                governance, and a vast app ecosystem under one secure, biometric identity.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Link to="/auth/register" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto px-10">
                    Join the Ecosystem
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/dashboard" className="w-full sm:w-auto">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto px-10">
                    Explore Demo
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Everything you need in one place</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              We've built a foundation that handles the complex stuff, so you can focus on connecting and creating.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 rounded-2xl border border-slate-100 bg-slate-50/50 hover:bg-white hover:shadow-xl hover:border-indigo-100 transition-all group"
              >
                <div className="h-12 w-12 rounded-xl bg-indigo-600 text-white flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-slate-900 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-indigo-600/10 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Our Core Services</h2>
              <p className="text-slate-400 text-lg mb-10">
                We provide a comprehensive suite of tools and services designed to empower users and developers alike. 
                From identity to infrastructure, we've got you covered.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="text-indigo-400 h-5 w-5 shrink-0" />
                    <span className="text-slate-200">{service}</span>
                  </div>
                ))}
              </div>
              <div className="mt-12">
                <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                  Read Documentation
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl bg-gradient-to-br from-indigo-500 to-purple-600 p-1">
                <div className="h-full w-full rounded-[22px] bg-slate-900 flex items-center justify-center overflow-hidden">
                   <div className="grid grid-cols-3 gap-4 p-8">
                      {[...Array(9)].map((_, i) => (
                        <div key={i} className="aspect-square rounded-xl bg-white/5 border border-white/10 animate-pulse" style={{ animationDelay: `${i * 150}ms` }} />
                      ))}
                   </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-2xl text-slate-900 max-w-xs">
                <div className="flex items-center space-x-3 mb-2">
                  <Zap className="text-amber-500 h-5 w-5" />
                  <span className="font-bold">Lightning Fast</span>
                </div>
                <p className="text-xs text-slate-500">Our distributed network ensures sub-second latency for all ecosystem actions.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="bg-indigo-600 rounded-3xl p-12 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 honeycomb-bg opacity-10" />
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-4xl font-bold mb-6">Ready to join the future?</h2>
              <p className="text-indigo-100 text-lg mb-10">
                Create your account in seconds and start exploring the Keskedo ecosystem. 
                No complex setups, just pure connection.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Link to="/auth/register" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-white text-indigo-600 hover:bg-slate-100 px-10">
                    Get Started Now
                  </Button>
                </Link>
                <Link to="/auth/login" className="w-full sm:w-auto">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white/10 px-10">
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-16">
        <div className="container mx-auto px-6 space-y-12">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
            <div className="col-span-2 md:col-span-1 space-y-3">
              <div className="flex items-center space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white">
                  <LayoutGrid size={20} />
                </div>
                <span className="text-xl font-bold text-slate-900">Keskedo</span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                A unified ecosystem for identity, governance, and connection.
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-slate-900">Product</h4>
              <ul className="space-y-2 text-slate-500">
                <li><Link to="/services" className="hover:text-indigo-600">Services</Link></li>
                <li><Link to="/features" className="hover:text-indigo-600">Features</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-slate-900">Company</h4>
              <ul className="space-y-2 text-slate-500">
                <li><Link to="/about" className="hover:text-indigo-600">About Us</Link></li>
                <li><Link to="/careers" className="hover:text-indigo-600">Careers</Link></li>
                <li><Link to="/press" className="hover:text-indigo-600">Press</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-slate-900">Support</h4>
              <ul className="space-y-2 text-slate-500">
                <li><Link to="/faq" className="hover:text-indigo-600">FAQ</Link></li>
                <li><Link to="/contact" className="hover:text-indigo-600">Contact</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-slate-900">Legal</h4>
              <ul className="space-y-2 text-slate-500">
                <li><Link to="/privacy" className="hover:text-indigo-600">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-indigo-600">Terms of Service</Link></li>
                <li><Link to="/cookies" className="hover:text-indigo-600">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-400">© 2026 Keskedo Ecosystem. All rights reserved.</p>
            <div className="flex gap-6 text-xs text-slate-400">
              <Link to="/privacy" className="hover:text-indigo-600">Privacy</Link>
              <Link to="/terms" className="hover:text-indigo-600">Terms</Link>
              <Link to="/cookies" className="hover:text-indigo-600">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Helper Badge component since it's used in Welcome
const Badge = ({ children, variant = 'primary', className }: { children: React.ReactNode, variant?: string, className?: string }) => {
  const variants: Record<string, string> = {
    primary: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    secondary: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold transition-colors ${variants[variant] || variants.primary} ${className}`}>
      {children}
    </span>
  );
};
